import { useNavigate } from 'react-router'

import type { RecipeV2Doc, RecipeV2Summary } from '@ctcells/api'
import { PATHS } from '@ctcells/core'
import { intlLocale, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { gate, type ScreenAccess } from '../access.ts'
import { DetailPage, OfflineNotice } from '../resourceParts.tsx'

import { RecipeDocBody } from './RecipeDocBody.tsx'
import { recipeTemplateRow } from './templateModel.ts'

import '../screens.css'
import '../resource.css'

/**
 * 템플릿 › **레시피** — 장비 전 구간(원심 · 어셈블러 · 염색)이 든 레시피 한 벌의 오른쪽 판
 * (eric 2026-10-12 「작업 준비에서.. 레시피 템플릿 탭을 나 만들어줘.. 저기서 표현해줘」 · 10-08 「템플릿 하나만.. 장비 다 들어가야 해」).
 * 목록 한 줄은 템플릿 한 목록(`TemplatesScreen` · 2026-10-13 한 목록 + 종류 필터)에 선다 — 이 파일은 오른쪽 판뿐이다.
 *
 * 🔴 **출처는 서버의 표준 레시피 하나뿐이다**(`GET /recipes/standard`) — 템플릿 탭에 서는 것은 이 한 벌뿐이다(데크 · 작업 · 랩웨어 · 시약
 *    템플릿 화면은 2026-10-26 걷었다). 이름 = `metadata.protocolName` · 분류 = `category` · `subcategory` 원문.
 * 🔴 **읽기 전용** — 서버에 레시피 템플릿 등록 · 수정 창구가 없다. 이 템플릿으로 새 레시피를 만드는 길(「＋ 레시피」 창)만 연다.
 */
export interface RecipeTemplateDetailProps {
  doc?: RecipeV2Doc | undefined
  /** `POST /recipes/summary` 결과 — 단계 · 시료 자리 · 랩웨어 */
  summary?: RecipeV2Summary | undefined
  loading?: boolean
  error?: string | undefined
  access?: ScreenAccess
  onClose: () => void
}

/**
 * 「레시피」 템플릿 한 건 — 오른쪽 판(eric 2026-10-13 「이 부분 너가 봐도 이상하지? 우리 화면 컨셉에 맞게 수정해줘..」).
 *
 * ```
 * CTCells 표준 시나리오 …                         [이 템플릿으로 레시피 만들기] ×
 * 레시피
 * ┌ 레시피 템플릿에만 있는 것 ─────────────┐   분류 · 명령 수 · 장비별 공정 기본값
 * ┌ 작업 차례 13 ──────────────────┐   차례(반복 포함) — 다른 템플릿과 잇거나 견주지 않는다(eric 2026-10-26 「응.. 다 빼줘」)
 * ┌ [원심분리기*] 어셈블러 염색기 ─────────┐   장비 스텝(염색기는 장비 스텝만 · 분주는 작업를 펼친 것)
 * ```
 *
 * 🔴 값은 서버 요약(`POST /recipes/summary`) 그대로다. 종전의 «·» 로 이어 붙인 맨 글자 목록을 열로 나눈 표로 바꿨다 — 레시피 추가 창의
 *    표와 같은 모양(`DataTable` `compact` · 긴 칸은 한 줄로 자르고 전문은 툴팁). 구역은 다른 상세 판과 같은 카드(`Card`).
 * 원심 · 어셈블러 · 염색 분주 스텝은 요약에 없고 문서(`designerApplication.data.process`)에 있다 — 아래 다섯 탭이 문서에서 읽는다(2026-10-13).
 * 단추 하나 — 「이 템플릿으로 레시피 만들기」(다른 상세 판의 주 단추와 같은 자리 · 크기). 권한(`control`)이 없으면 비활성 + 사유.
 */
export function RecipeTemplateDetail({
  doc,
  summary,
  loading = false,
  error,
  access = {},
  onClose,
}: RecipeTemplateDetailProps) {
  const navigate = useNavigate()
  const row = doc === undefined ? undefined : recipeTemplateRow(doc)
  return (
    <DetailPage
      actions={
        /* 제목 줄 오른쪽 끝(eric 2026-10-13 「이 템플릿으로 레시피 만들기는 오른쪽에」) — `.rtpl__make` 가 단추 줄을 오른쪽으로 민다 */
        <span className="rtpl__make">
          <Button
            size="pill"
            variant="primary"
            {...gate(access, { perm: 'control' })}
            onClick={() => void navigate(PATHS.recipeNew)}
          >
            {t('action.recipeFromTemplate')}
          </Button>
        </span>
      }
      onClose={onClose}
      submeta={t('tab.template.recipe')}
      title={row?.name ?? t('tab.template.recipe')}
    >
      <OfflineNotice shown={access.offline === true} />
      {error === undefined ? null : (
        <p className="deny" role="alert">
          {error}
        </p>
      )}
      {loading || summary === undefined || doc === undefined ? (
        error === undefined ? (
          <p className="muted">{t('state.loading')}</p>
        ) : null
      ) : (
        <RecipeDocBody
          doc={doc}
          headRows={[
            ...(row === undefined || row.category === ''
              ? []
              : [{ key: 'category', label: t('column.category'), value: row.category }]),
            {
              key: 'commands',
              label: t('recipeTemplate.commands'),
              value: summary.commands.total.toLocaleString(intlLocale()),
            },
          ]}
          summary={summary}
        />
      )}
    </DetailPage>
  )
}
